import { format } from "date-fns";

/**
 * Utilitário para padronização de nomes de arquivos PDF
 * Formato: CLIENTE_TipoDocumento_Numero.pdf
 */

export interface PdfFileNameConfig {
  includeDate?: boolean;
  includeSeller?: boolean;
  dateFormat?: string;
  separator?: string;
  numberWithZeros?: boolean;
}

export interface PdfFileNameExtras {
  date?: Date;
  sellerName?: string;
}

/**
 * Sanitiza o nome para uso em arquivos
 * Remove acentos, caracteres especiais e converte espaços em underscores
 */
export function sanitizeFileName(name: string, separator: string = '_'): string {
  return name
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^A-Z0-9\s]/g, '')     // Remove caracteres especiais
    .trim()
    .replace(/\s+/g, separator);     // Espaços viram separador
}

/**
 * Gera nome de arquivo PDF padronizado
 * @param customerName Nome do cliente
 * @param docType Tipo do documento (Venda, Recibo, Contrato, etc.)
 * @param docNumber Número do documento
 * @param config Configurações opcionais de formatação
 * @param extras Dados adicionais opcionais (data, vendedor)
 * @returns Nome do arquivo no formato configurado
 */
export function generatePdfFileName(
  customerName: string | undefined | null,
  docType: string,
  docNumber: string,
  config?: PdfFileNameConfig,
  extras?: PdfFileNameExtras
): string {
  const sep = config?.separator || '_';
  const customer = sanitizeFileName(customerName || 'CLIENTE', sep);
  
  let number = docNumber.replace(/[^A-Z0-9]/gi, '');
  if (config?.numberWithZeros === false) {
    number = number.replace(/^0+/, '') || '0';
  }

  const parts = [customer, docType, number];

  if (config?.includeDate && extras?.date) {
    const dateStr = format(extras.date, config.dateFormat || 'dd-MM-yyyy');
    parts.push(dateStr);
  }

  if (config?.includeSeller && extras?.sellerName) {
    parts.push(sanitizeFileName(extras.sellerName, sep));
  }

  return parts.join(sep) + '.pdf';
}

/**
 * P4 (§I.142-F5): nome padrão de documento fiscal baixado —
 * NFe_{serie}_{numero}.{ext} / NFCe_{serie}_{numero}.{ext}.
 * "NFCe" sem hífen/barra (caracteres proibidos em filename); série e número
 * saneados para alfanumérico. Vale para PDF (DANFE/DANFCE) e XML.
 */
export function fiscalDocFileName(
  invoiceType: 'nfe' | 'nfce',
  series: string | null | undefined,
  invoiceNumber: string | null | undefined,
  ext: 'pdf' | 'xml',
): string {
  const tipo = invoiceType === 'nfce' ? 'NFCe' : 'NFe';
  const clean = (v: string | null | undefined) =>
    (v ?? '').replace(/[^A-Za-z0-9]/g, '') || 'SN';
  return `${tipo}_${clean(series)}_${clean(invoiceNumber)}.${ext}`;
}

/**
 * Gera nome de arquivo PDF padronizado para boletos bancários
 * Formato: NOME_CLIENTE_BOLETO_<docType>_123.pdf (docType default: VENDA)
 */
export function generateBoletoFileName(
  customerName?: string | null,
  saleNumber?: string | number | null,
  docType: string = 'VENDA'
): string {
  const customer = sanitizeFileName(customerName || 'CLIENTE');
  const number = saleNumber ? String(saleNumber) : 'SN';
  return `${customer}_BOLETO_${sanitizeFileName(docType)}_${number}.pdf`;
}
